import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { FormattingState, FormatType } from '@/types/formatting.types';
import { StylePreset } from '@/lib/constants/presets';
import { useEditorStore } from './editorStore';
import { UnicodeConverter } from '@/lib/formatters/unicodeConverter';

export const useFormattingStore = create<FormattingState>()(
  immer((set) => ({
    selectedFormat: null,
    formatHistory: [],

    applyFormat: (format: FormatType) => {
      set((state) => {
        state.selectedFormat = format;
        state.formatHistory.push({ type: format, timestamp: Date.now() });
      });
      // Logic for generic format application if needed
    },

    applyUnicodeBold: () => {
      const quill = useEditorStore.getState().quillInstance;
      if (!quill) return;

      const selection = quill.getSelection();
      if (selection && selection.length > 0) {
        const text = quill.getText(selection.index, selection.length);
        const boldText = UnicodeConverter.toBold(text);
        quill.deleteText(selection.index, selection.length);
        quill.insertText(selection.index, boldText);
        // Reselect
        quill.setSelection(selection.index, boldText.length);
      }
    },

    applyUnicodeItalic: () => {
      const quill = useEditorStore.getState().quillInstance;
      if (!quill) return;

      const selection = quill.getSelection();
      if (selection && selection.length > 0) {
        const text = quill.getText(selection.index, selection.length);
        const italicText = UnicodeConverter.toItalic(text);
        quill.deleteText(selection.index, selection.length);
        quill.insertText(selection.index, italicText);
        quill.setSelection(selection.index, italicText.length);
      }
    },

    applyUnderline: () => {
      const quill = useEditorStore.getState().quillInstance;
      if (!quill) return;

      const selection = quill.getSelection();
      if (selection && selection.length > 0) {
        const text = quill.getText(selection.index, selection.length);
        const underlined = UnicodeConverter.toUnderline(text);
        quill.deleteText(selection.index, selection.length);
        quill.insertText(selection.index, underlined);
        quill.setSelection(selection.index, underlined.length);
      }
    },

    addBulletPoints: () => {
      const quill = useEditorStore.getState().quillInstance;
      if (!quill) return;
      quill.format('list', 'bullet');
    },

    addNumbering: () => {
      const quill = useEditorStore.getState().quillInstance;
      if (!quill) return;
      quill.format('list', 'ordered');
    },

    addEmojiBullets: () => {
      // This is a bit more complex. We might want to replace the current list with emoji bullets
      // or insert an emoji at the start of the line.
      // For simplicity: Insert a random emoji bullet at cursor.
      const quill = useEditorStore.getState().quillInstance;
      if (!quill) return;
      
      const selection = quill.getSelection();
      if (selection) {
        quill.insertText(selection.index, "✨ ");
      }
    },

    addLineBreaks: () => {
       const quill = useEditorStore.getState().quillInstance;
      if (!quill) return;
      
      const selection = quill.getSelection();
      if (selection) {
        quill.insertText(selection.index, "\n");
      }
    },

    addSectionDividers: () => {
      const quill = useEditorStore.getState().quillInstance;
      if (!quill) return;
      
      const selection = quill.getSelection();
      if (selection) {
        quill.insertText(selection.index, "\n━━━━━━━━━━━━━━━━━━━━━━━━\n");
      }
    },

    capitalizeText: () => {
      const quill = useEditorStore.getState().quillInstance;
      if (!quill) return;

      const selection = quill.getSelection();
      if (selection && selection.length > 0) {
        const text = quill.getText(selection.index, selection.length);
        const upper = text.toUpperCase();
        quill.deleteText(selection.index, selection.length);
        quill.insertText(selection.index, upper);
        quill.setSelection(selection.index, upper.length);
      }
    },

    applyPreset: (preset: StylePreset) => {
      // Apply preset logic
      // e.g., set header type, casing, etc.
      console.log('Applying preset:', preset.name);
      // Implementation depends on how we want to apply it to existing content or new content
    }
  }))
);
